import ORGANIZATION_API from 'api/organization/v1'
import PAYMENT_PROFILES_API from 'api/payment-profiles/v1'
import { push } from 'connected-react-router'
import { change, startSubmit, stopSubmit } from 'redux-form'
import { call, debounce, put, select, takeLatest } from 'redux-saga/effects'
import extractErrors from 'utils/extractErrors'
import { OPF, TAX_SYSTEM } from 'modules/Finance/PaymentProfiles/Create/dataMapper'
import { ROUTES } from 'constants/routes'
import { generatePath } from 'react-router'
import { notificationShow } from 'modules/Notifications/actions'
import {
  CREATE_PAYMENT_PROFILE,
  GET_ORGANIZATION_BANK,
  GET_ORGANIZATION_SUGGEST,
  GET_PAYMENT_PROFILES,
  GET_PAYMENT_PROFILE_BY_ID,
  SET_PAYMENT_PROFILE_DATA,
  SET_PAYMENT_PROFILES_DATA,
} from 'modules/Finance/PaymentProfiles/store/types'

function* fetchPaymentProfiles({ payload }) {
  try {
    yield put({
      type: SET_PAYMENT_PROFILES_DATA,
      payload: { loading: true },
    })

    const { response, error } = yield call(PAYMENT_PROFILES_API.fetchProfiles, {
      params: payload,
    })

    if (error) {
      throw error
    }

    const { data, pagination } = response
    yield put({
      type: SET_PAYMENT_PROFILES_DATA,
      payload: { data, pagination },
    })
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    yield put({
      type: SET_PAYMENT_PROFILES_DATA,
      payload: { failed: false },
    })
    console.logXHR(error)
  } finally {
    yield put({
      type: SET_PAYMENT_PROFILES_DATA,
      payload: { loading: false, loaded: true },
    })
  }
}

function* fetchPaymentProfileById({ payload }) {
  const { profileId, meta: { resolve, reject } = {} } = payload
  try {
    yield put({
      type: SET_PAYMENT_PROFILE_DATA,
      payload: { loading: true },
    })

    const { response, error } = yield call(PAYMENT_PROFILES_API.fetchProfileById, {
      profileId,
    })

    if (error) {
      throw error
    }

    const { data } = response
    yield put({
      type: SET_PAYMENT_PROFILE_DATA,
      payload: { data },
    })
    resolve()
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    yield put({
      type: SET_PAYMENT_PROFILE_DATA,
      payload: { failed: false },
    })
    reject()
    console.logXHR(error)
  } finally {
    yield put({
      type: SET_PAYMENT_PROFILE_DATA,
      payload: { loading: false, loaded: true },
    })
  }
}

function* createPaymentProfile({ payload }) {
  const {
    meta: { resolve = () => {}, reject = () => {} } = {},
    profileId,
    formName,
    ...rest
  } = payload

  const { i18n } = yield select(({ app }) => app)

  try {
    yield put(startSubmit(formName))

    const { error } = yield call(
      profileId ? PAYMENT_PROFILES_API.update : PAYMENT_PROFILES_API.create,
      {
        profileId,
        data: { ...rest },
      },
    )

    if (error) {
      throw error
    }

    yield put(push(generatePath(ROUTES.FINANCE_PAYMENT_PROFILES, { i18n })))

    yield put(
      notificationShow({
        type: 'success',
        message: 'payment_profiles::new.notifications.success',
        delay: 10000,
      }),
    )
    yield put(stopSubmit(formName))
    resolve()
  } catch (error) {
    const { form, fields } = extractErrors(error.errors)

    if (Object.keys(fields).length) {
      yield put(stopSubmit(formName, fields))
    }

    if (form) {
      yield put(
        notificationShow({
          type: 'error',
          message: form,
          delay: 10000,
        }),
      )
    }
    reject()

    console.logXHR(error)
  }
}

function* getOrganizationSuggest({ payload, formName, meta: { resolve = () => {} } = {} }) {
  const { asyncErrors } = yield select((state) => state['form'][formName])

  if (asyncErrors?.details?.tax_number) {
    resolve()
    return
  }

  try {
    const { response, error } = yield call(ORGANIZATION_API.suggestOrganization, {
      params: payload,
    })

    if (error) {
      throw error
    }

    if (response?.data) {
      const { name, opf, tax_system, kpp, ogrn, ogrnip, address, management } = response.data

      yield put(change(formName, 'details.organisation_name', name))
      yield put(change(formName, 'details.legal_personality', OPF?.[opf] ?? ''))
      yield put(change(formName, 'details.tax_system', TAX_SYSTEM?.[tax_system] ?? ''))

      if (kpp) {
        yield put(change(formName, 'details.kpp', kpp))
      }

      if (ogrn) {
        yield put(change(formName, 'details.ogrn', ogrn))
      }

      if (ogrnip) {
        yield put(change(formName, 'details.ogrnip', ogrnip))
      }

      if (address) {
        yield put(change(formName, 'details.juridical_address', address))
      }

      if (management) {
        yield put(change(formName, 'details.general_director', management))
      }
    }
  } catch (error) {
    console.logXHR(error)
  } finally {
    resolve()
  }
}

function* getOrganizationBank({ payload, formName, meta: { resolve = () => {} } = {} }) {
  const { asyncErrors } = yield select((state) => state['form'][formName])

  if (asyncErrors?.details?.bic) {
    resolve()
    return
  }

  try {
    const { response, error } = yield call(ORGANIZATION_API.fetchBankInfo, {
      params: payload,
    })

    if (error) {
      throw error
    }

    if (response?.data) {
      const { name, correspondent_account } = response.data

      yield put(change(formName, 'details.bank_name', name))
      yield put(change(formName, 'details.correspondent_account', correspondent_account))
    }
  } catch (error) {
    console.logXHR(error)
  } finally {
    resolve()
  }
}

export default function* Sagas() {
  yield takeLatest(GET_PAYMENT_PROFILES, fetchPaymentProfiles)
  yield takeLatest(GET_PAYMENT_PROFILE_BY_ID, fetchPaymentProfileById)
  yield takeLatest(CREATE_PAYMENT_PROFILE, createPaymentProfile)
  yield debounce(500, GET_ORGANIZATION_SUGGEST, getOrganizationSuggest)
  yield debounce(500, GET_ORGANIZATION_BANK, getOrganizationBank)
}
